// src/features/collaboration/components/Post.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Avatar,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Menu,
  Chip,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  LinearProgress,
  Badge,
  Checkbox,
  Radio,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions
} from '@mui/material';
import { 
  Favorite as FavoriteIcon, 
  FavoriteBorder as FavoriteBorderIcon,
  Comment as CommentIcon,
  Share as ShareIcon,
  VerifiedSharp,
  MoreVert as MoreVertIcon,
  YouTube as YouTubeIcon,
  PictureAsPdf as PdfIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  ArticleOutlined as ArticleIcon,
  Poll as PollIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ZoomIn as ZoomInIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { THEME_COLORS } from '../../../styles/theme';
import { savePost, reportPost, deletePost, pinPost, unpinPost, voteOnPoll } from '../services/collaborationService';
import ShareMenu from './ShareMenu';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config'; 
import EventCard from './EventCard'; 
import MathRenderer from '../../../components/math/MathRenderer';
import InlineMathField from '../../../components/math/InlineMathField';


// Helper function to safely format dates
const safeFormatDate = (dateValue) => {
  if (!dateValue) return '';
  
  try {
    // Handle different date formats
    let date;
    if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else if (dateValue.seconds) {
      // Handle Firestore Timestamp objects
      date = new Date(dateValue.seconds * 1000);
    } else {
      return '';
    }
    
    // Check if date is valid before formatting
    if (isNaN(date.getTime())) {
      return '';
    }
    
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

const getPostDocuments = (post) => {
  if (Array.isArray(post?.documents) && post.documents.length > 0) {
    return post.documents.filter((document) => document?.url);
  }

  const legacyDocumentUrl = post?.documentFile || post?.documentUrl || '';
  if (!legacyDocumentUrl) {
    return [];
  }

  return [{
    name: post?.documentName || 'Document.pdf',
    url: legacyDocumentUrl,
    path: post?.imagePath || ''
  }];
};

const isRenderableImage = (url = '') => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  if (url.startsWith('data:image/')) {
    return true;
  }

  if (/\.pdf(\?|$)/i.test(url)) {
    return false;
  }

  return /^https?:\/\//i.test(url) || /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url);
};

const URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+/gi;

const extractUrlsFromText = (text = '') => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  return Array.from(new Set((text.match(URL_PATTERN) || []).map((url) => url.replace(/[),.!?]+$/, ''))));
};

const getHostname = (url = '') => {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch (error) {
    return '';
  }
};

const buildFaviconUrl = (url = '') => {
  const hostname = getHostname(url);
  return hostname ? `https://www.google.com/s2/favicons?domain=${hostname}&sz=64` : '';
};

const getPreviewImage = (preview = {}) => (
  preview.image || preview.imageUrl || preview.ogImage || preview.thumbnail || preview.thumbnailUrl || ''
);

const normalizeLinkPreview = (preview) => {
  if (!preview || typeof preview !== 'object') {
    return null;
  }

  const url = preview.url || preview.href || preview.link || '';
  if (!/^https?:\/\//i.test(url)) {
    return null;
  }

  const hostname = getHostname(url);
  const image = getPreviewImage(preview);

  return {
    url,
    title: preview.title || preview.ogTitle || hostname || url,
    description: preview.description || preview.ogDescription || preview.summary || '',
    image,
    siteName: preview.siteName || preview.publisher || hostname,
    hostname,
    favicon: preview.favicon || preview.icon || buildFaviconUrl(url)
  };
};

const getPostLinkPreviews = (post, articleData = {}) => {
  const previewFields = [
    post?.linkPreview,
    post?.urlPreview,
    post?.preview,
    ...(Array.isArray(post?.linkPreviews) ? post.linkPreviews : []),
    ...(Array.isArray(post?.urlPreviews) ? post.urlPreviews : []),
    ...(Array.isArray(post?.previews) ? post.previews : [])
  ];

  const normalizedPreviews = previewFields
    .map(normalizeLinkPreview)
    .filter(Boolean);

  const extractedUrls = [
    ...extractUrlsFromText(post?.content),
    ...extractUrlsFromText(articleData?.body),
    ...extractUrlsFromText(articleData?.subtitle)
  ];

  const excludedUrls = new Set([
    post?.image,
    post?.mediaUrl,
    post?.videoUrl,
    post?.videoFile,
    post?.documentFile,
    post?.documentUrl,
    ...getPostDocuments(post).map((document) => document?.url)
  ].filter(Boolean));

  const previewMap = new Map();

  normalizedPreviews.forEach((preview) => {
    if (!excludedUrls.has(preview.url)) {
      previewMap.set(preview.url, preview);
    }
  });

  extractedUrls.forEach((url) => {
    if (excludedUrls.has(url) || previewMap.has(url)) {
      return;
    }

    const hostname = getHostname(url);
    previewMap.set(url, {
      url,
      title: hostname || url,
      description: '',
      image: '',
      siteName: hostname,
      hostname,
      favicon: buildFaviconUrl(url)
    });
  });

  return Array.from(previewMap.values());
};

const Post = ({ post, onLike, onComment, onShare, onEdit, onDelete, fullView = false }) => {
  const navigate = useNavigate();
  const [liked, setLiked] = useState(post?.likedByCurrentUser || false);
  const [likesCount, setLikesCount] = useState(post?.likes || 0);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState(post?.comments || []);
  const [zoomedImage, setZoomedImage] = useState(null);
  const { userProfile, currentUser } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [eventPreview, setEventPreview] = useState(false);
  const [expandedArticle, setExpandedArticle] = useState(false);
  const [expandedPost, setExpandedPost] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showPoll, setShowPoll] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [pollData, setPollData] = useState(post?.poll || null);
  const [hasVoted, setHasVoted] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  
  const authorFirstName = post?.author?.firstName || '';
  const authorLastName = post?.author?.lastName || '';
  // Fall back to legacy 'name' field or use firstName + lastName
  const authorName = (authorFirstName && authorLastName) 
    ? `${authorFirstName} ${authorLastName}`
    : post?.author?.name || 'Anonymous';
  const authorInitial = authorFirstName ? authorFirstName.charAt(0) : (authorName ? authorName.charAt(0) : '?');
  const authorImage = post?.author?.profileImage || null;
  const authorTitle = post?.author?.title || '';
  const authorRole = post?.author?.role || post?.author?.roles?.[0] || '';
  const authorSubjects = post?.author?.subjects || [];
  const isVerified = post?.author?.verified || false;

  // Improved checks for current user data
  const userFirstName = userProfile?.firstName || '';
  const userLastName = userProfile?.lastName || '';
  const userName = (userFirstName && userLastName)
    ? `${userFirstName} ${userLastName}`
    : userProfile?.name || 'User';
  const userInitial = userFirstName ? userFirstName.charAt(0) : (userName ? userName.charAt(0) : '?');
  const userImage = userProfile?.profileImage || null;
  
  // Safely format date if it exists
  const formattedDate = safeFormatDate(post?.createdAt);
  const isResearch = post?.postType === 'research';
  const isArticle = post?.postType === 'article' || (!isResearch && Boolean(post?.article?.title || post?.article?.body));
  const isLongForm = isArticle || isResearch;
  const articleData = post?.article || {};
  const subjects = post?.subjects || [];
  const grades = post?.grades || [];
  const category = post?.category || '';
  const shouldClampArticle = Boolean(!fullView && articleData?.body && (articleData.body.length > 800 || articleData.body.split('\n').length > 8));
  const shouldClampPost = Boolean(post?.content && (post.content.length > 500 || post.content.split('\n').length > 8));
  const isPinned = Boolean(post?.pinnedAt);
  const hasPoll = Boolean(pollData?.question);
  const linkPreviews = getPostLinkPreviews(post, articleData);
  const compactActionButtonSx = {
    width: 32,
    height: 32,
    p: 0,
    transition: 'transform 0.2s',
    '& .MuiSvgIcon-root': {
      fontSize: 16
    },
    '&:hover': { transform: 'scale(1.1)' }
  };
  
  useEffect(() => {
    console.log('Author data:', post?.author);
  }, [post]);

  useEffect(() => {
    let active = true;

    const checkUserVote = async () => {
      if (!hasPoll || !currentUser?.uid || !post?.id) {
        if (active) setHasVoted(false);
        return;
      }

      try {
        const voterSnap = await getDoc(doc(db, 'posts', post.id, 'pollVoters', currentUser.uid));
        if (active) {
          setHasVoted(voterSnap.exists());
          if (voterSnap.exists()) {
            const selected = voterSnap.data()?.selectedOptionIds || [];
            setSelectedOptions(Array.isArray(selected) ? selected : []);
          }
        }
      } catch (error) {
        console.error('Failed to check poll vote status:', error);
        if (active) setHasVoted(false);
      }
    };

    checkUserVote();
    return () => { active = false; };
  }, [hasPoll, currentUser?.uid, post?.id]);

  const handleLike = async () => {
    try {
      if (onLike && userProfile?.id) {
        await onLike(post.id, userProfile.id);
      }
      setLiked(!liked);
      setLikesCount(liked ? likesCount - 1 : likesCount + 1);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  useEffect(() => {
    let mounted = true;
    const loadEvent = async () => {
      try {
        if (post?.eventId) {
          const evRef = doc(db, 'events', post.eventId);
          const evSnap = await getDoc(evRef);
          if (evSnap.exists() && mounted) {
            setEventPreview({ id: evSnap.id, ...evSnap.data() });
          }
        }
      } catch (err) {
        console.error('Failed to load event for post', err);
      }
    };

    loadEvent();
    return () => { mounted = false; };
  }, [post?.eventId]);
  
  const handleComment = async () => {
    if (!commentText.trim() || !userProfile?.id) return;
    
    try {
      if (onComment) {
        const newComment = await onComment(post.id, {
          userId: userProfile.id,
          text: commentText,
          createdAt: new Date().toISOString()
        });
        
        if (newComment) {
          setComments([...comments, newComment]);
        }
      }
      setCommentText('');
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };
  
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleShareMenuOpen = (event) => {
    setShareAnchorEl(event.currentTarget);
  };
  
  const handleShareMenuClose = () => {
    setShareAnchorEl(null);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSavePost = async () => {
    handleMenuClose();
    if (!currentUser?.uid) {
      showSnackbar('Sign in to save posts.', 'warning');
      return;
    }
    try {
      await savePost(currentUser.uid, post.id);
      showSnackbar('Post saved.');
    } catch (error) {
      console.error('Error saving post:', error);
      showSnackbar('Failed to save post. Please try again.', 'error');
    }
  };

  const handleReportPost = async () => {
    handleMenuClose();
    if (!currentUser?.uid) {
      showSnackbar('Sign in to report posts.', 'warning');
      return;
    }
    const reason = window.prompt('Tell us what is wrong (optional):', '');
    if (reason === null) return;
    try {
      await reportPost(currentUser.uid, post.id, reason);
      showSnackbar('Thanks for reporting. We will review this content.');
    } catch (error) {
      console.error('Error reporting post:', error);
      showSnackbar('Failed to report post. Please try again.', 'error');
    }
  };

  const handleDeletePost = async () => {
    handleMenuClose();
    if (!currentUser?.uid) {
      showSnackbar('Sign in to delete posts.', 'warning');
      return;
    }
    const confirmed = window.confirm('Delete this post? This cannot be undone.');
    if (!confirmed) return;
    try {
      await deletePost(post.id);
      showSnackbar('Post deleted.');
      if (onDelete) {
        onDelete(post.id);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      showSnackbar('Failed to delete post. Please try again.', 'error');
    }
  };

  const handleTogglePin = async () => {
    handleMenuClose();
    if (!currentUser?.uid) {
      showSnackbar('Sign in to pin posts.', 'warning');
      return;
    }
    try {
      if (isPinned) {
        await unpinPost(post.id, currentUser.uid);
        showSnackbar('Post unpinned.');
      } else {
        await pinPost(post.id, currentUser.uid);
        showSnackbar('Post pinned to the top.');
      }
    } catch (error) {
      console.error('Error pinning post:', error);
      const message = error?.message === 'Pin limit reached'
        ? 'Only two posts can be pinned at a time.'
        : 'Failed to update pin. Please try again.';
      showSnackbar(message, 'error');
    }
  };
  
  const handleOptionToggle = (optionId) => {
    setSelectedOptions(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleOptionSelect = (optionId) => {
    setSelectedOptions([optionId]);
  };

  const handleVote = async () => {
    if (!currentUser?.uid || !selectedOptions.length) return;
    try {
      const result = await voteOnPoll(post.id, currentUser.uid, selectedOptions);
      setPollData(result.poll);
      setHasVoted(true);
      setSelectedOptions([]);
      showSnackbar('Vote recorded!');
    } catch (error) {
      showSnackbar(error.message || 'Failed to vote. Please try again.', 'error');
    }
  };

  const openAuthDialog = () => {
    setAuthDialogOpen(true);
  };

  const closeAuthDialog = () => {
    setAuthDialogOpen(false);
  };

  const executeExternalAction = (url, { download = false } = {}) => {
    if (!url) {
      return;
    }

    if (download) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = '';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleProtectedExternalAction = (event, url, options = {}) => {
    if (!url) {
      return;
    }

    if (!currentUser) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      openAuthDialog();
      return;
    }

    if (options.programmatic) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      executeExternalAction(url, options);
    }
  };

  const handleRichTextClickCapture = (event) => {
    const anchor = event.target?.closest?.('a[href]');
    if (!anchor) {
      return;
    }

    handleProtectedExternalAction(event, anchor.href);
  };

const renderMedia = () => {
  if (!post) return null;
  
  // Handle existing post structure with direct image property
  if (post.image && (!post.mediaType || post.mediaType === 'image') && isRenderableImage(post.image)) {
    return (
      <Box
        sx={{
          width: '100%',
          borderRadius: 0,
          overflow: 'hidden',
          mt: 1,
          mb: 1,
          cursor: 'pointer',
          position: 'relative',
          '&:hover .zoom-overlay': { opacity: 1 }
        }}
        onClick={() => setZoomedImage(post.image)}
      >
        <img
          src={post.image}
          alt="Post attachment"
          style={{
            width: '100%',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
        <Box
          className="zoom-overlay"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.5)',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s'
          }}
        >
          <ZoomInIcon sx={{ color: '#fff', fontSize: 20 }} />
        </Box>
      </Box>
    );
  }

  // Handle new media type-based structure
  if (post.mediaType === 'image' || (post.image && isRenderableImage(post.image))) {
    const imageSrc = post.image || post.mediaUrl;
    if (imageSrc) {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: 0,
            overflow: 'hidden',
            mt: 1,
            mb: 1,
            cursor: 'pointer',
            position: 'relative',
            '&:hover .zoom-overlay': { opacity: 1 }
          }}
          onClick={() => setZoomedImage(imageSrc)}
        >
          <img
            src={imageSrc}
            alt="Post attachment"
            style={{
              width: '100%',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
          <Box
            className="zoom-overlay"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.5)',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s'
            }}
          >
            <ZoomInIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
        </Box>
      );
    }
  }
    
    if (post.mediaType === 'video') {
      if (post.videoFile) {
        return (
          <Box 
            sx={{ 
              width: '100%', 
              borderRadius: 0, 
              overflow: 'hidden',
              mt: 1, 
              mb: 1 
            }}
          >
            <video 
              autoPlay
              muted
              loop
              playsInline
              controls
              width="100%"
              style={{ borderRadius: 0 }}
            >
              <source src={post.videoFile} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </Box>
        );
      } else if (post.videoUrl) {
        
        let embedUrl = post.videoUrl;
        if (post.videoUrlType === 'youtube' && embedUrl.includes('youtube.com/embed/')) {
          embedUrl = `${embedUrl}?autoplay=1&mute=1&loop=1&playlist=${embedUrl.split('/').pop()}`;
        } else if (post.videoUrlType === 'tiktok') {
          embedUrl = `${embedUrl}?autoplay=1&muted=1`;
        }
        
        return (
          <Box 
            sx={{ 
              width: '100%', 
              borderRadius: 0, 
              overflow: 'hidden',
              mt: 1, 
              mb: 1,
              position: 'relative',
              paddingTop: '56.25%', // 16:9 aspect ratio
            }}
          >
            <iframe
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0
              }}
              src={embedUrl}
              title="Video"
              allowFullScreen
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </Box>
        );
      }
    }
    
    return null;
  };

  const renderDocuments = () => {
    const documents = getPostDocuments(post);
    if (documents.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 1, mb: 1 }}>
        {documents.map((document, index) => (
          <Box
            key={`${document.url}-${index}`}
            sx={{
              width: '100%',
              p: 2,
              mb: 1,
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              overflow: 'hidden',
              minWidth: 0
            }}
          >
            <Box display="flex" alignItems="center" sx={{ minWidth: 0, flex: 1, mr: 1 }}>
              <PdfIcon sx={{ color: '#f44336', fontSize: 40, mr: 2, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {document.name || `Document ${index + 1}.pdf`}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  PDF Document
                </Typography>
              </Box>
            </Box>
            <Box sx={{ flexShrink: 0, display: 'flex' }}>
              <IconButton
                size="small"
                title="Open in new tab"
                onClick={(event) => handleProtectedExternalAction(event, document.url, { programmatic: true })}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="Download"
                onClick={(event) => handleProtectedExternalAction(event, document.url, { download: true, programmatic: true })}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderLinkPreviews = () => {
    if (linkPreviews.length === 0) {
      return null;
    }

    return (
      <Box sx={{ px: 2, pb: 1 }}>
        {linkPreviews.map((preview) => (
          <Box
            key={preview.url}
            component="button"
            type="button"
            onClick={(event) => handleProtectedExternalAction(event, preview.url, { programmatic: true })}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              textDecoration: 'none',
              color: 'inherit',
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 1.5,
              backgroundColor: '#fff',
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              appearance: 'none',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: '#e0e0e0',
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            {preview.image ? (
              <Box
                sx={{
                  width: { xs: '100%', sm: 180 },
                  minWidth: { xs: '100%', sm: 180 },
                  height: { xs: 180, sm: 'auto' },
                  backgroundColor: '#f5f5f5'
                }}
              >
                <img
                  src={preview.image}
                  alt={preview.title || preview.siteName || 'Link preview'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block'
                  }}
                />
              </Box>
            ) : null}
            <Box sx={{ p: 2, minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, minWidth: 0 }}>
                {preview.favicon ? (
                  <Box
                    component="img"
                    src={preview.favicon}
                    alt=""
                    sx={{ width: 18, height: 18, borderRadius: '4px', flexShrink: 0 }}
                  />
                ) : (
                  <ArticleIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                )}
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {preview.siteName || preview.hostname || 'External link'}
                </Typography>
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  mb: preview.description ? 0.5 : 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {preview.title || preview.url}
              </Typography>
              {preview.description ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {preview.description}
                </Typography>
              ) : null}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {preview.url}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };
  
  return (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
      <CardHeader
        avatar={
          <Tooltip title={authorName}>
            <Avatar 
              src={authorImage} 
              alt={authorName}
              sx={{ 
                width: 48, 
                height: 48, 
                bgcolor: THEME_COLORS.primary,
                border: '2px solid #fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              {authorInitial}
            </Avatar>
          </Tooltip>
        }
        action={
          <IconButton aria-label="settings" onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
        }
        title={
          <Box display="flex" alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mr: 0.5 }}>
              {authorName}
            </Typography>
            {isVerified && (
              <VerifiedSharp sx={{ mr: 0.5, width: 16, height: 16, color: THEME_COLORS.primary }} />
            )}
            {authorRole && (
              <Typography 
                variant="body2" 
                color="textSecondary" 
                sx={{ 
                  textTransform: 'capitalize', 
                  mr: 0.5,
                  backgroundColor: 'rgba(39, 35, 97, 0.08)', 
                  px: 0.7, 
                  py: 0.2, 
                  borderRadius: 1,
                  fontSize: '0.7rem'
                }}
              >
                {authorRole}
              </Typography>
            )}
            {authorSubjects && authorSubjects.length > 0 && (
              <>
                {authorSubjects.slice(0, 2).map((subject, index) => (
                  <Chip
                    key={index}
                    label={subject}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      mr: 0.5,
                      bgcolor: 'rgba(39, 35, 97, 0.08)',
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                ))}
                {authorSubjects.length > 2 && (
                  <Typography 
                    variant="caption" 
                    color="textSecondary" 
                    sx={{ alignSelf: 'center', fontSize: '0.65rem' }}
                  >
                    +{authorSubjects.length - 2} more
                  </Typography>
                )}
              </>
            )}
          </Box>
        }
        subheader={
          <Box>
            <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
              {formattedDate}
            </Typography>
          </Box>
        }
        sx={{ pb: 1 }}
      />
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {userProfile?.id === post?.author?.id && (
          <MenuItem onClick={() => { handleMenuClose(); onEdit?.(post); }}>Edit Post</MenuItem>
        )}
        <MenuItem onClick={handleSavePost}>Save Post</MenuItem>
        <MenuItem onClick={handleReportPost}>Report Content</MenuItem>
        {userProfile?.id === post?.author?.id && (
          <MenuItem onClick={handleTogglePin}>{isPinned ? 'Unpin Post' : 'Pin Post'}</MenuItem>
        )}
        {userProfile?.id === post?.author?.id && (
          <MenuItem onClick={handleDeletePost}>Delete Post</MenuItem>
        )}
      </Menu>
      
      <CardContent sx={{ py: 1, px: 0 }}>
        {(category || subjects.length > 0 || grades.length > 0) && (
          <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {category && (
              <Chip
                label={category}
                size="small"
                sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)', fontWeight: 600 }}
              />
            )}
            {subjects.map((subject) => (
              <Chip
                key={`subject-${subject}`}
                label={subject}
                size="small"
                variant="outlined"
              />
            ))}
            {grades.map((grade) => (
              <Chip
                key={`grade-${grade}`}
                label={grade}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        )}
        {isLongForm && (
          <Box sx={{ px: 2, pt: 1, pb: 2, bgcolor: '#f7f7fb', borderRadius: 2, mx: 2, mb: 2, border: '1px solid #e6e6f0' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip
                icon={<ArticleIcon sx={{ fontSize: 16 }} />}
                label={isResearch ? 'Deep Research' : 'Article'}
                size="small"
                sx={{
                  bgcolor: 'rgba(33, 150, 243, 0.12)',
                  color: '#0d47a1',
                  fontWeight: 600
                }}
              />
              {articleData.wordCount ? (
                <Typography variant="caption" color="textSecondary">
                  {articleData.wordCount} words
                </Typography>
              ) : null}
            </Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {articleData.title || (isResearch ? 'Untitled Research' : 'Untitled Article')}
            </Typography>
            {articleData.subtitle ? (
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
                {articleData.subtitle}
              </Typography>
            ) : null}
            <Box
              sx={
                shouldClampArticle && !expandedArticle
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 8,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }
                  : undefined
              }
            >
              <Box onClickCapture={handleRichTextClickCapture}>
                <MathRenderer
                  content={articleData.body || ''}
                />
              </Box>
            </Box>
            {shouldClampArticle && (
              <Button
                size="small"
                onClick={() => {
                  if (isResearch && !fullView) {
                    navigate(`/post/${post.id}`);
                    return;
                  }
                  setExpandedArticle(!expandedArticle);
                }}
                sx={{ mt: 1 }}
              >
                {isResearch && !fullView ? 'Show more' : (expandedArticle ? 'Show less' : 'Show more')}
              </Button>
            )}
          </Box>
        )}

        {post?.content ? (
          <Box sx={{ px: 2, pb: 1 }}>
            <Box
              sx={
                shouldClampPost && !expandedPost
                  ? {
                      display: '-webkit-box',
                      WebkitLineClamp: 8,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }
                  : undefined
              }
            >
              <Box onClickCapture={handleRichTextClickCapture}>
                <MathRenderer
                  content={post.content}
                />
              </Box>
            </Box>
            {shouldClampPost && (
              <Button
                size="small"
                onClick={() => setExpandedPost(!expandedPost)}
                sx={{ mt: 1 }}
              >
                {expandedPost ? 'Show less' : 'Show more'}
              </Button>
            )}
          </Box>
        ) : null}

        {renderLinkPreviews()}
        
        {/* Poll question at foot of content */}
        {hasPoll && (
          <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PollIcon sx={{ fontSize: 18, color: THEME_COLORS.primary }} />
            <Typography variant="body2" fontWeight="bold" color={THEME_COLORS.primary}>
              {pollData.question}
            </Typography>
          </Box>
        )}

        {eventPreview ? (
          <Box sx={{ px: 2, pb: 1 }}>
            <EventCard event={eventPreview} compact />
            <Button size="small" onClick={() => navigate(`/events/${eventPreview.id}`)}>View event</Button>
          </Box>
        ) : (
          <>
            {renderMedia()}
            {renderDocuments()}
          </>
        )}
        
        {/* Likes and comments section */}
        <Box display="flex" alignItems="center" mt={2} px={2}>
          <Typography variant="body2" color="textSecondary">
            {likesCount} {likesCount === 1 ? 'like' : 'likes'} • {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
            {post?.shareCount > 0 && ` • ${post.shareCount} ${post.shareCount === 1 ? 'share' : 'shares'}`}
            {hasPoll && ` • ${pollData.totalVotes || 0} ${(pollData.totalVotes || 0) === 1 ? 'vote' : 'votes'}`}
          </Typography>
        </Box>
      </CardContent>

      
      <Divider />
      
      <CardActions disableSpacing>
        <IconButton 
          onClick={handleLike}
          sx={{ 
            ...compactActionButtonSx,
            color: liked ? '#e53935' : 'inherit',
          }}
        >
          {liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>
        <IconButton 
          onClick={() => setShowComments(!showComments)}
          sx={compactActionButtonSx}
        >
          <CommentIcon />
        </IconButton>
        <IconButton
          onClick={handleShareMenuOpen}
          sx={compactActionButtonSx}
        >
          <ShareIcon />
        </IconButton>
        {hasPoll && (
          <IconButton
            onClick={() => setShowPoll(!showPoll)}
            sx={compactActionButtonSx}
          >
            <Badge
              color="primary"
              variant="dot"
              invisible={hasVoted || !currentUser}
            >
              <PollIcon />
            </Badge>
          </IconButton>
        )}
      </CardActions>
      
      {/* Share Menu */}
      <ShareMenu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareMenuClose}
        post={post}
        onShare={onShare}
      />
      
      {/* Collapsible Poll Section */}
      <Collapse in={showPoll && hasPoll}>
        <Box p={2} pt={0}>
          <Divider sx={{ mt: 1, mb: 2 }} />
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PollIcon sx={{ fontSize: 18 }} />
            {pollData?.question}
          </Typography>

          {!currentUser ? (
            <Box textAlign="center" py={2}>
              <Typography variant="body2" color="textSecondary">
                Sign in to vote on this poll.
              </Typography>
            </Box>
          ) : !hasVoted ? (
            /* Voting controls */
            <Box>
              {pollData?.options?.map((option) => (
                <Box
                  key={option.id}
                  display="flex"
                  alignItems="center"
                  sx={{
                    mb: 1,
                    p: 1,
                    border: '1px solid',
                    borderColor: selectedOptions.includes(option.id) ? THEME_COLORS.primary : '#e0e0e0',
                    borderRadius: 1.5,
                    cursor: 'pointer',
                    bgcolor: selectedOptions.includes(option.id) ? 'rgba(39, 35, 97, 0.04)' : 'transparent',
                    '&:hover': { borderColor: THEME_COLORS.primary, bgcolor: 'rgba(39, 35, 97, 0.02)' }
                  }}
                  onClick={() => pollData.allowMultiple ? handleOptionToggle(option.id) : handleOptionSelect(option.id)}
                >
                  {pollData.allowMultiple ? (
                    <Checkbox
                      checked={selectedOptions.includes(option.id)}
                      size="small"
                      sx={{ p: 0.5, mr: 1, color: THEME_COLORS.primary, '&.Mui-checked': { color: THEME_COLORS.primary } }}
                    />
                  ) : (
                    <Radio
                      checked={selectedOptions.includes(option.id)}
                      size="small"
                      sx={{ p: 0.5, mr: 1, color: THEME_COLORS.primary, '&.Mui-checked': { color: THEME_COLORS.primary } }}
                    />
                  )}
                  <Typography variant="body2">{option.text}</Typography>
                </Box>
              ))}
              <Button
                variant="contained"
                size="small"
                onClick={handleVote}
                disabled={selectedOptions.length === 0}
                sx={{
                  mt: 1,
                  backgroundColor: THEME_COLORS.primary,
                  '&:hover': { backgroundColor: '#1e1c4e' }
                }}
              >
                Vote
              </Button>
            </Box>
          ) : (
            /* Results view */
            <Box>
              {pollData?.options?.map((option) => {
                const voteCount = pollData.votes?.[option.id] || 0;
                const total = pollData.totalVotes || 0;
                const pct = total > 0 ? Math.round((voteCount / total) * 100) : 0;
                return (
                  <Box key={option.id} sx={{ mb: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2">{option.text}</Typography>
                        {hasVoted && selectedOptions.length === 0 && (
                          /* Show check for voted options when user has already voted */
                          null
                        )}
                      </Box>
                      <Typography variant="caption" color="textSecondary" fontWeight="bold">
                        {pct}% ({voteCount})
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: THEME_COLORS.primary,
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>
                );
              })}
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: 14 }} />
                {pollData.totalVotes || 0} total {(pollData.totalVotes || 0) === 1 ? 'vote' : 'votes'}
                {hasVoted && ' • You voted'}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>

      {showComments && (
        <Box p={2} pt={0}>
          <Divider sx={{ mt: 1, mb: 2 }} />
          
          {comments.map((comment, index) => (
            <Box key={comment.id || `comment-${index}`} display="flex" mb={2}>
              <Avatar 
                src={comment?.user?.profileImage} 
                alt={comment?.user?.name || 'User'}
                sx={{ width: 32, height: 32, mr: 1.5 }}
              >
                {comment?.user?.name ? comment.user.name.charAt(0) : '?'}
              </Avatar>
              <Box 
                sx={{ 
                  bgcolor: '#f5f5f5', 
                  borderRadius: 2, 
                  p: 1.5, 
                  flex: 1 
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold">
                  {comment?.user?.name || 'User'}
                </Typography>
                <Box onClickCapture={handleRichTextClickCapture}>
                  <MathRenderer
                    content={comment?.text || ''}
                    style={{ fontSize: '0.85rem' }}
                  />
                </Box>
                <Typography variant="caption" color="textSecondary">
                  {safeFormatDate(comment?.createdAt)}
                </Typography>
              </Box>
            </Box>
          ))}
          
          {!currentUser ? (
            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="textSecondary">
                Sign in to comment on this post.
              </Typography>
            </Box>
          ) : (
            <Box display="flex" mt={2}>
              <Avatar 
                src={userImage} 
                alt={userName}
                sx={{ width: 32, height: 32, mr: 1.5 }}
              >
                {userInitial}
              </Avatar>
              <Box flex={1} display="flex" alignItems="flex-start">
                <Box sx={{ flex: 1, mr: 1 }}>
                  <InlineMathField
                    value={commentText}
                    onChange={setCommentText}
                    placeholder="Add a comment..."
                    rows={2}
                  />
                </Box>
                <Button 
                  variant="contained" 
                  onClick={handleComment}
                  disabled={!commentText.trim()}
                  sx={{ 
                    backgroundColor: THEME_COLORS.primary,
                    '&:hover': { backgroundColor: '#1e1c4e' },
                    mt: 0.5
                  }}
                >
                  Post
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Image Zoom Dialog */}
      <Dialog
        open={Boolean(zoomedImage)}
        onClose={() => setZoomedImage(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            boxShadow: 'none',
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            minHeight: '60vh'
          }}
        >
          <IconButton
            onClick={() => setZoomedImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          <IconButton
            title="Download image"
            onClick={(event) => handleProtectedExternalAction(event, zoomedImage, { download: true, programmatic: true })}
            sx={{
              position: 'absolute',
              top: 8,
              right: 56,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              zIndex: 1
            }}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            title="Open in new tab"
            onClick={(event) => handleProtectedExternalAction(event, zoomedImage, { programmatic: true })}
            sx={{
              position: 'absolute',
              top: 8,
              right: 104,
              color: '#fff',
              bgcolor: 'rgba(255,255,255,0.15)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
              zIndex: 1
            }}
          >
            <OpenInNewIcon />
          </IconButton>
          {zoomedImage && (
            <img
              src={zoomedImage}
              alt="Zoomed attachment"
              style={{
                maxWidth: '100%',
                maxHeight: '85vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={authDialogOpen}
        onClose={closeAuthDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Sign in to continue</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Opening external links and downloading files from public posts requires an account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={closeAuthDialog}>Cancel</Button>
          <Button
            variant="outlined"
            onClick={() => {
              closeAuthDialog();
              navigate('/register');
            }}
          >
            Sign Up
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              closeAuthDialog();
              navigate('/login');
            }}
            sx={{
              backgroundColor: THEME_COLORS.primary,
              '&:hover': { backgroundColor: '#1e1c4e' }
            }}
          >
            Sign In
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};
export default Post;
